<template>
  <div>
  <dsa-loading v-if="loading" text="Loading dashboard…" />
  <div v-else-if="!hasRegistration">
    <div class="welcome">
      <div>
        <h1>Welcome to DSA, {{ firstName }}</h1>
        <p class="sub">Complete your registration to unlock 6 exam attempts valid for 3 years.</p>
      </div>
      <span class="badge-muted">No active registration</span>
    </div>
    <div v-for="n in dashboardNotices" :key="n.code" class="notice" :class="{ warn: n.type === 'warning' }">{{ n.message }}</div>
    <div v-for="b in bulletins" :key="'b-' + b.id" class="notice">
      <b v-if="b.title">{{ b.title }}:</b> {{ b.message }}
    </div>
    <div class="cta-card">
      <div>
        <h2>Get started with DSA</h2>
        <div class="feat">
          <span>6 exam attempts</span>
          <span>3-year validity</span>
          <span>Repeated attempts allowed (max three attempts per subject)</span>
        </div>
        <div class="price">
          <span v-if="showListPrice" class="list-price">{{ pricing.currency }} {{ pricing.list_price }}</span>
          Your price <b>{{ pricing.currency }} {{ pricing.your_price }}</b><br>
          <span style="font-size:12.5px">{{ pricing.note }}</span>
        </div>
      </div>
      <div class="cta-right">
        <button type="button" class="btn btn-primary btn-big" @click="startPurchase">
          Register now — {{ pricing.currency }} {{ pricing.your_price }}
        </button>
        <div style="margin-top:10px"><router-link to="/student/catalog" class="entry">Browse the catalog</router-link></div>
      </div>
    </div>
    <div class="notice"><b>Browse freely, act on registration:</b> you can explore the subject catalog and testing windows without a registration — but registering for an exam requires an active registration first.</div>
    <div class="section" v-if="upcoming.length">
      <div class="section-head"><h2>My exams</h2></div>
      <table>
        <thead>
          <tr>
            <th>Subject</th>
            <th>Window</th>
            <th>Exam Date</th>
            <th>Status</th>
            <th style="text-align:right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="exam in upcoming" :key="exam.application_id">
            <td>
              <span class="subject">
                {{ exam.subject_name }}
                <small>{{ exam.category || exam.subject_code }}</small>
              </span>
            </td>
            <td>{{ windowLabel(exam) }}</td>
            <td>{{ exam.exam_date || '—' }}</td>
            <td><span class="chip pend">Pending</span></td>
            <td class="td-actions" style="text-align:right">
              <button
                type="button"
                class="entry"
                :disabled="redeemingId === exam.application_id"
                @click="redeem(exam)"
              >{{ redeemingId === exam.application_id ? 'Redeeming…' : 'Redeem' }}</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="section" v-else>
      <div class="section-head"><h2>My attempts</h2></div>
      <div class="empty">
        <h3>No attempts yet</h3>
        <p>Your registered exams and upcoming testing windows will appear here. Register first, then pick a subject and a window to begin.</p>
        <div class="actions" style="justify-content:center">
          <router-link to="/student/catalog" class="btn btn-outline">Browse subject catalog</router-link>
        </div>
      </div>
    </div>
    <div class="section">
      <div class="section-head"><h2>Recent scores</h2></div>
      <div class="empty" style="padding:28px 24px">
        <h3>No scores yet</h3>
        <p style="margin-bottom:0">Scores are released within 7 working days after a testing window closes.</p>
      </div>
    </div>
  </div>
  <div v-else>
    <div class="welcome">
      <div>
        <h1>Welcome back, {{ firstName }}</h1>
        <p class="sub">Your 6-attempt, 3-year DSA registration is active and ready.</p>
      </div>
      <span class="badge">{{ activeBadge }}</span>
    </div>
    <div v-for="n in dashboardNotices" :key="n.code" class="notice" :class="{ warn: n.type === 'warning' }">{{ n.message }}</div>
    <div v-for="b in bulletins" :key="'b-' + b.id" class="notice">
      <b v-if="b.title">{{ b.title }}:</b> {{ b.message }}
    </div>
    <div class="stats">
      <div class="card">
        <div class="label">Attempts remaining</div>
        <div class="value">{{ wallet.remaining_quota || 0 }} <small>of {{ wallet.total_quota || 6 }}</small></div>
        <div class="bar"><i :style="{ width: quotaPercent + '%' }"></i></div>
        <div class="hint">{{ wallet.used_quota || 0 }} used · {{ subjectCount }} subject{{ subjectCount === 1 ? '' : 's' }}</div>
      </div>
      <div class="card">
        <div class="label">Registration valid until</div>
        <div class="value">{{ formatExpire(wallet.expire_at) }}</div>
        <div class="hint">{{ purchaseHint }}</div>
      </div>
      <div class="card">
        <div class="label">Subjects attempted</div>
        <div class="value">{{ subjectCount }}</div>
        <div class="hint">Max 3 attempts per subject</div>
      </div>
    </div>
    <div class="section">
      <div class="section-head"><h2>Quick actions</h2></div>
      <div class="actions">
        <router-link to="/student/catalog" class="btn btn-primary">Register for an exam</router-link>
        <button type="button" class="btn btn-external" @click="openPrep">Prepare for my exam</button>
        <button
          v-if="readyExams.length"
          type="button"
          class="btn btn-external"
          @click="viewExam(readyExams[0])"
        >View exam</button>
      </div>
    </div>
    <div class="section" v-if="upcoming.length">
      <div class="section-head"><h2>My exams</h2></div>
      <table>
        <thead>
          <tr>
            <th>Subject</th>
            <th>Window</th>
            <th>Attempt</th>
            <th>Exam Date</th>
            <th>Status</th>
            <th style="text-align:right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="exam in upcoming" :key="exam.application_id">
            <td>
              <span class="subject">
                {{ exam.subject_name }}
                <small>{{ exam.category || exam.subject_code }}<template v-if="exam.attempt_num > 1"> · Attempt {{ exam.attempt_num }}</template></small>
              </span>
            </td>
            <td>{{ windowLabel(exam) }}</td>
            <td>{{ exam.attempt_num || 1 }}</td>
            <td>{{ exam.exam_date || '—' }}</td>
            <td>
              <span class="chip" :class="isPending(exam) ? 'pend' : 'soon'">
                {{ isPending(exam) ? 'Pending' : 'Scheduled' }}
              </span>
            </td>
            <td class="td-actions" style="text-align:right">
              <template v-if="isPending(exam)">
                <button
                  type="button"
                  class="entry"
                  :disabled="redeemingId === exam.application_id"
                  @click="redeem(exam)"
                >{{ redeemingId === exam.application_id ? 'Redeeming…' : 'Redeem' }}</button>
              </template>
              <template v-else>
                <button type="button" class="entry" @click="viewExam(exam)">View exam</button>
                <button
                  v-if="hasPrep(exam)"
                  type="button"
                  class="entry ext"
                  @click="openPrep(exam)"
                >Prep</button>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="section" v-if="completed.length">
      <div class="section-head">
        <h2>My scores</h2>
        <router-link to="/student/attempts">View details</router-link>
      </div>
      <div v-for="item in completed" :key="item.application_id" class="score-row">
        <div>
          <div class="s-subject">{{ item.subject_name }}<template v-if="item.attempt_num"> · Attempt {{ item.attempt_num }}</template></div>
          <div class="s-meta">{{ scoreMeta(item) }}</div>
        </div>
        <div class="score-row-right">
          <div v-if="scoreView(item.score)" class="s-num">
            {{ scoreView(item.score).total }}
            <small>/ {{ scoreView(item.score).full }} · Consolidated {{ scoreView(item.score).consolidated }}</small>
          </div>
          <span v-else class="chip pend">Results pending</span>
          <button
            v-if="scoreReleased(item.score)"
            type="button"
            class="entry"
            @click="openReport(item.application_id)"
          >View report</button>
        </div>
      </div>
    </div>

    <exam-schedule-modal
      :open="examModalOpen"
      :loading="examModalLoading"
      :subject-name="examModalSubject"
      :project-name="examModalProject"
      :sessions="examModalSessions"
      :exam-system-url="examSystemUrl"
      @close="examModalOpen = false"
    />
  </div>
  <redeem-pass-modal
    :open="redeemModalOpen"
    :step="redeemStep"
    :subject-name="redeemSubjectName"
    :busy="!!redeemingId"
    @close="closeRedeemModal"
    @confirm="confirmRedeem"
    @purchase="goPurchasePass"
  />
  </div>
</template>

<script>
import { getStudentDashboard, redeemDsaApplicationWithPass } from '@/api/dsa'
import { getUserId, getUserName, logout } from '@/utils/store'
import { formatExpire, formatDate, formatScoreDisplay, formatQuarterWindow, openPrepExternal, isPendingPayment } from '@/utils/dsaFormat'
import { loadExamSchedule } from '@/utils/examEntry'
import ExamScheduleModal from '@/components/ExamScheduleModal.vue'
import RedeemPassModal from '@/components/RedeemPassModal.vue'

export default {
  name: 'StudentDashboard',
  components: { ExamScheduleModal, RedeemPassModal },
  data() {
    return {
      loading: true,
      wallet: {},
      upcoming: [],
      completed: [],
      subjectCount: 0,
      notices: [],
      bulletins: [],
      prepUrl: '',
      redeemingId: '',
      redeemModalOpen: false,
      redeemStep: 'confirm',
      redeemSubjectName: '',
      redeemTarget: null,
      examModalOpen: false,
      examModalLoading: false,
      examModalSubject: '',
      examModalProject: '',
      examModalSessions: [],
      examSystemUrl: 'https://exam.learningfirst.io/',
      pricing: {
        currency: 'USD',
        list_price: 239,
        your_price: 159,
        note: 'Base USD 239 · automatic region & school reduction applied',
      },
    }
  },
  computed: {
    firstName() {
      const name = getUserName() || 'Student'
      return name.split(' ')[0]
    },
    hasRegistration() {
      return (this.wallet.total_quota || 0) > 0 || (this.wallet.passes && this.wallet.passes.length > 0)
    },
    readyExams() {
      return this.upcoming.filter(e => !isPendingPayment(e))
    },
    quotaPercent() {
      if (!this.wallet.total_quota) return 0
      return Math.min(100, Math.round(((this.wallet.used_quota || 0) / this.wallet.total_quota) * 100))
    },
    activeBadge() {
      if (this.wallet.paid_label) return `Registration active · Paid ${this.wallet.paid_label}`
      return `Registration active · ${this.wallet.remaining_quota || 0} attempts left`
    },
    purchaseHint() {
      if (this.wallet.purchased_at) {
        return `Purchased ${formatDate(this.wallet.purchased_at)} · 3-year validity`
      }
      return '3-year validity from Pass purchase'
    },
    dashboardNotices() {
      const hidden = 'Welcome to DSA. Scores are typically released within seven working days after a testing window closes. Max three attempts per subject.'
      return (this.notices || []).filter(n => String(n && n.message).trim() !== hidden)
    },
    showListPrice() {
      const list = Number(this.pricing.list_price)
      const price = Number(this.pricing.your_price)
      return list > 0 && price > 0 && list > price
    },
  },
  mounted() {
    if (this.$route.query.paid === '1') {
      this.$emit('toast', 'Payment successful — your DSA Pass is active')
      this.$router.replace({ path: '/student/dashboard' })
    }
    this.load()
  },
  methods: {
    formatExpire,
    scoreView(score) {
      return formatScoreDisplay(score)
    },
    scoreReleased(score) {
      const sv = formatScoreDisplay(score)
      return !!(sv && score && score.visible !== false && !score.pending)
    },
    openReport(application_id) {
      this.$router.push(`/student/transcript/${application_id}`)
    },
    scoreMeta(item) {
      const win = this.windowLabel(item)
      const sv = formatScoreDisplay(item.score)
      if (sv && item.score && item.score.released_at) {
        return `${win} · Released ${formatDate(item.score.released_at)}`
      }
      if (item.exam_date) return `${win} · Exam taken ${item.exam_date}`
      return win
    },
    startPurchase() {
      this.$router.push('/student/pass')
    },
    windowLabel(item) {
      if (!item) return '—'
      if (item.window_label) return item.window_label
      return formatQuarterWindow(item.quarter_code)
    },
    hasPrep(item) {
      return !!(item && (item.has_case_material || (item.prep_url && String(item.prep_url).trim())))
    },
    isPending(item) {
      return isPendingPayment(item)
    },
    openPrep(item) {
      if (item && this.hasPrep(item)) {
        openPrepExternal(item.prep_url)
        return
      }
      openPrepExternal(this.prepUrl)
    },
    async viewExam(exam) {
      const applicationId = exam && (exam.application_id || exam)
      if (!applicationId) return
      this.examModalOpen = true
      this.examModalLoading = true
      this.examModalSubject = (exam && exam.subject_name) || ''
      this.examModalProject = ''
      this.examModalSessions = []
      const data = await loadExamSchedule(this, applicationId)
      this.examModalLoading = false
      if (!data) {
        this.examModalOpen = false
        return
      }
      this.examModalSubject = data.subject_name || this.examModalSubject
      this.examModalProject = data.project_name || ''
      this.examModalSessions = data.exam_sessions || []
      if (data.exam_system_url) this.examSystemUrl = data.exam_system_url
    },
    async redeem(exam) {
      this.redeemTarget = exam
      this.redeemSubjectName = (exam && exam.subject_name) || 'this subject'
      this.redeemStep = 'confirm'
      this.redeemModalOpen = true
    },
    closeRedeemModal() {
      if (this.redeemingId) return
      this.redeemModalOpen = false
      this.redeemTarget = null
      this.redeemStep = 'confirm'
    },
    goPurchasePass() {
      this.redeemModalOpen = false
      this.redeemTarget = null
      this.redeemStep = 'confirm'
      this.$router.push('/student/pass')
    },
    async confirmRedeem() {
      const exam = this.redeemTarget
      if (!exam || !exam.application_id) return
      this.redeemingId = exam.application_id
      try {
        const res = await redeemDsaApplicationWithPass(getUserId(), exam.application_id)
        if (res.data && res.data.code === 0) {
          this.$emit('toast', 'Pass redeemed')
          this.redeemModalOpen = false
          this.redeemTarget = null
          this.redeemStep = 'confirm'
          await this.load()
        } else if (res.data && res.data.code === -5) {
          this.redeemStep = 'no_pass'
        } else {
          this.$emit('toast', (res.data && res.data.msg) || 'Redeem failed')
        }
      } catch (e) {
        this.$emit('toast', 'Redeem failed')
      } finally {
        this.redeemingId = ''
      }
    },
    async load() {
      const student_id = getUserId()
      if (!student_id) {
        this.loading = false
        this.$router.replace('/login')
        return
      }
      try {
        const res = await getStudentDashboard(student_id)
        if (res.data && res.data.code === 0) {
          const data = res.data.data || {}
          this.wallet = data.pass_wallet || {}
          this.upcoming = data.upcoming_exams || []
          this.completed = data.completed_exams || []
          this.notices = data.notices || []
          this.bulletins = data.bulletins || []
          this.prepUrl = (data.prep && data.prep.url) || ''
          this.subjectCount = data.subjects_attempted != null
            ? data.subjects_attempted
            : new Set(this.completed.concat(this.upcoming).map(e => e.subject_code)).size
          if (data.pass_pricing) this.pricing = data.pass_pricing
        } else {
          // Stale cookie / invalid student → clear session so landing page shows login
          logout()
          this.$router.replace('/login')
          return
        }
      } catch (e) {
        this.$emit('toast', 'Failed to load dashboard')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.list-price{display:inline-block;margin-right:8px;text-decoration:line-through;color:var(--muted)}
.score-row-right{display:flex;align-items:center;gap:14px}
</style>
